import { BrandKit } from '../BrandKit/BrandKitForm/BrandKitForm.types'
import {
  DHEditorInstance,
  DHEditorLib,
  DHLibConfig,
  DHTco,
  DHUserMode
} from '../../../types/design-huddle.types'
import { DesignHuddleScript } from './DesignHuddleScript'
import {
  FC,
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { MAX_WAIT_FOR_DH_SCRIPT_RETRIES } from './constants'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { useBrandKitTco, useDHConfig } from './hooks'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'

export interface DesignHuddleState {
  dhLibIsReady: boolean
  brandKitTco: DHTco
  brandKit?: BrandKit
  DHEditorLib?: DHEditorLib
  dhEditorInstanceRef: MutableRefObject<DHEditorInstance | undefined>
}

interface DesignHuddleProviderProps {
  mode: DHUserMode
  children: ReactNode
}

/**
 * Design Huddle context object for use in our custom {@link useDesignHuddle} hook.
 *
 * Inspired by [How to use React Context effectively](https://kentcdodds.com/blog/how-to-use-react-context-effectively)
 * by Kent C. Dodds.
 */
export const DesignHuddleContext = createContext<DesignHuddleState | undefined>(undefined)

/**
 * Context provider for DesignHuddleContext. Allows for child components to access DesignHuddleContext
 * through the {@link useDesignHuddle} hook.
 */
export const DesignHuddleProvider: FC<DesignHuddleProviderProps> = ({ mode, children }) => {
  const isClient = useIsMounted()
  const [scriptIsLoaded, setScriptIsLoaded] = useState<boolean>(
    isClient() && !!window?.DSHDEditorLib
  )
  const brandKit = useCodeSelector((state: RootCodeState) => state.printStore.brandKit)
  const brandKitTco: DHTco = useBrandKitTco()
  const dhConfig: DHLibConfig = useDHConfig({ mode })
  const [DHEditorLib, setDHEditorLib] = useState<DHEditorLib | undefined>(undefined)
  const dhEditorInstanceRef = useRef<DHEditorInstance | undefined>(undefined)
  const dhLibIsReady = useMemo(() => scriptIsLoaded && !!DHEditorLib, [DHEditorLib, scriptIsLoaded])

  const waitLoops = useRef<number>(0)

  const onDHScriptLoaded = useCallback((): void => {
    if (!scriptIsLoaded) {
      if (!window?.DSHDEditorLib) {
        if (waitLoops.current < MAX_WAIT_FOR_DH_SCRIPT_RETRIES) {
          waitLoops.current += 1
          requestAnimationFrame(onDHScriptLoaded)
        } else {
          throw new Error(
            `Design Huddle script failed to load after ${MAX_WAIT_FOR_DH_SCRIPT_RETRIES} attempts.`
          )
        }
      } else {
        window.DSHDEditorLib?.configure(dhConfig)
        setScriptIsLoaded(true)
        setDHEditorLib(window.DSHDEditorLib)
      }
    }
  }, [dhConfig, scriptIsLoaded])

  useEffect(() => {
    if (!scriptIsLoaded) {
      onDHScriptLoaded()
    }
  }, [scriptIsLoaded, onDHScriptLoaded])

  const value: DesignHuddleState = {
    dhLibIsReady,
    brandKitTco,
    brandKit,
    DHEditorLib,
    dhEditorInstanceRef
  }

  return (
    <>
      <DesignHuddleScript onLoad={onDHScriptLoaded} />
      <DesignHuddleContext.Provider value={value}>{children}</DesignHuddleContext.Provider>
    </>
  )
}
